import { Box } from '@mui/material'
import isEmpty from 'lodash/isEmpty'
import type { HTMLAttributes } from 'react'
import { FormattedMessage } from 'react-intl'

import { useFormatMessage } from '@acre/utils'
import { CaseSearchResultItem, MortgageReason } from '@acre/graphql'
import { CaseStatusTag } from '@acre/design-system'

import { getProductTypeIntl } from '../GlobalSearchOption/GlobalSearchOption.helpers'
import { getDisplayedAddress, getMultipleClientNameTag } from './GlobalCaseSearchOption.helpers'
import { CaseStatusBox, CaseTypeBox, ClientNamesBox } from '../GlobalSearchOption/GlobalSearchOption.styles'
import { AddressBox, Container } from './GlobalCaseSearchOption.styles'

export const DEFAULT_EMPTY_OPTION = '-'

/**
 * Main GlobalCaseSearchOption component
 *  - handles the case when option is empty and renders a "not found" message
 *  - otherwise renders the GlobalCaseSearchResultOption when an option exist
 */
const GlobalCaseSearchOption = (props: HTMLAttributes<HTMLLIElement>, option: any): JSX.Element => {
  if (isEmpty(option)) {
    return (
      <Box key="option-no-results">
        <FormattedMessage id="generic.nothingToShow" />
      </Box>
    )
  }
  return <GlobalCaseSearchResultOption {...props} {...option} key={`option-${props.id}`} />
}

/**
 * Renders the active and inactive cases information within the case search results
 */
const GlobalCaseSearchResultOption = (
  props: CaseSearchResultItem & {
    overrideName?: string
    focused?: boolean
  },
) => {
  const {
    Clients: clients,
    id,
    case_status,
    case_reason,
    address,
    focused,
    overrideName,
    // Extract complex objects that can't be spread as DOM props at runtime
    // These cause "Cannot convert object to primitive value" errors when spread to Container
    GeneralInsurance: _GeneralInsurance,
    Mortgages: _Mortgages,
    Protection: _Protection,
    ...rest
  } = props

  const formatMessage = useFormatMessage()

  return (
    <Container {...rest} focused={focused}>
      <ClientNamesBox>{overrideName ?? getMultipleClientNameTag(clients)}</ClientNamesBox>
      <CaseTypeBox>
        <FormattedMessage id={getProductTypeIntl(case_reason || MortgageReason.InvalidMortgageReason)} />
      </CaseTypeBox>
      <CaseStatusBox>
        <CaseStatusTag status={case_status} key={`status_tag_${id}`} />
      </CaseStatusBox>
      <AddressBox>{`${formatMessage('nav.search.property')} ${getDisplayedAddress(address)}`}</AddressBox>
    </Container>
  )
}
export default GlobalCaseSearchOption
